import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { useAuth, roleLabel, displayName } from '../lib/auth'
import { Brand } from './Brand'
import { isHidden, MODULES, navFor, modulesFor, moduleOf, type ModuleKey, type Entitlements } from '../lib/nav'
import { api } from '../lib/api'
import { staffLive } from '../lib/live'
import SearchPalette from './SearchPalette'
import FindOnPage from './FindOnPage'
import ProgressBar from './ProgressBar'
import { markAction } from '../lib/progress'

/**
 * Two letters for the avatar. A one-word name gets one letter, and an empty name falls
 * back to whatever else identifies the person rather than rendering a question mark at
 * somebody who is perfectly well signed in.
 */
/**
 * Where each page was last scrolled to. Module scope so it survives the component
 * remounting on every navigation, and deliberately not sessionStorage: this is a
 * convenience for the current visit, not something to persist.
 */
const scrollMemory = new Map<string, number>()

function initials(...candidates: string[]) {
  for (const c of candidates) {
    const parts = String(c || '').trim().split(/[\s@._-]+/).filter(Boolean)
    if (parts.length) return parts.slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  }
  return '\u00b7'
}

function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

const REMEMBERED = 'lynqpod_module'

export default function Layout() {
  const { user, signOut } = useAuth()
  const { pathname } = useLocation()
  // A page change counts as something the person did, however it was started (lib/progress.ts).
  // A layout effect, because those all run before any page's own useEffect starts fetching.
  useLayoutEffect(() => { markAction() }, [pathname])
  const navType = useNavigationType()

  /**
   * ⚠️ SCROLL DID NOT RESET BETWEEN PAGES. Scrolling a long table and then clicking
   * anything in the sidebar landed you 487px down a page you had never seen, which reads
   * as a page that failed to load its top half. The browser does this for a real
   * document; a single-page app has to do it itself.
   *
   * On BACK it is the opposite: returning to a list should return you to where you were
   * in it, so a POP is left alone and the browser's own restoration applies.
   */
  /** <main> is the scroll container now, so every scroll operation goes through it. */
  const scroller = useRef<HTMLElement | null>(null)

  /**
   * ⚠️ navType IS READ THROUGH A REF, NOT A DEPENDENCY, AND THAT IS THE WHOLE FIX.
   *
   * React Router flips navType to PUSH in a render where pathname is STILL THE OLD PAGE.
   * With navType in the dependency array the effect below fired with the page you were
   * leaving and reset its remembered position to 0, so BACK never had anything to
   * restore. The map held 0 for every page, always.
   *
   * It was invisible while the window did the scrolling, because browsers restore window
   * scroll themselves and landed you roughly, if not exactly, back. Once <main> scrolls
   * instead there is nothing to cover for it: a browser does not restore an arbitrary
   * container, so it showed up as landing at the top every time.
   */
  const navTypeRef = useRef(navType)
  navTypeRef.current = navType

  useEffect(() => {
    if (navTypeRef.current !== 'POP') {
      scrollMemory.set(pathname, 0)
      if (scroller.current) scroller.current.scrollTop = 0
      return
    }
    /**
     * ⚠️ RESTORING ON BACK HAS TO WAIT FOR THE DATA. Every page here fetches after it
     * mounts, so at the moment the browser restores scroll the page is still a spinner
     * and there is nothing to scroll to: asking for 600px landed at 199, because that
     * was the whole height at the time. So the target is re-applied for a short window
     * while the content grows, and stops as soon as it is reached.
     */
    const want = scrollMemory.get(pathname) || 0
    if (!want) return
    /**
     * ⚠️ THE WINDOW HAS TO OUTLAST THE SLOWEST PAGE. At 20 tries of 60ms it gave up
     * after 1.2 seconds, and Debtors renders 421 rows well after that, so the restore
     * clamped to whatever the spinner's height allowed and stopped. Four seconds covers
     * the slowest page here; it stops the moment it lands, so a fast page pays nothing.
     */
    let tries = 0
    const id = window.setInterval(() => {
      const el = scroller.current
      if (!el) return
      el.scrollTop = want
      if (++tries > 40 || Math.abs(el.scrollTop - want) < 2) window.clearInterval(id)
    }, 100)
    return () => window.clearInterval(id)
  }, [pathname])

  // Remember where each page was left, so BACK can put you back in the list rather than
  // at the top of it.
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const save = () => scrollMemory.set(pathname, el.scrollTop)
    el.addEventListener('scroll', save, { passive: true })
    /**
     * ⚠️ DO NOT save() ON THE WAY OUT. The cleanup runs AFTER React has swapped the new
     * page into this same <main>, so scrollTop has already been reset by the content
     * change and saving it writes 0 over the position we spent the whole page recording.
     * The listener has kept the map current on every scroll; there is nothing left to do
     * here but let go.
     */
    return () => el.removeEventListener('scroll', save)
  }, [pathname])
  const navigate = useNavigate()

  /**
   * Which modules this practice has bought. Undefined until it loads, which shows
   * everything: flashing an empty sidebar on every page load to avoid briefly
   * showing a door that will not open is the worse trade. The server refuses the
   * endpoints either way, so nothing here is load bearing for access.
   */
  const [entitlements, setEntitlements] = useState<Entitlements>(undefined)
  useEffect(() => {
    if (!user) return
    api.get<{ modules: Record<string, boolean> }>('/modules')
      .then((r) => setEntitlements(r.data.modules))
      .catch(() => setEntitlements(undefined))
  }, [user])

  // A teammate login (accountRole 'staff') sees only the modules it was given, as far as the
  // practice has them. "Who buys what" comes with Inventory. The server enforces the same.
  const staff = user?.accountRole === 'staff'
  const staffAccess = (user?.access ?? []).join(',')
  const allowed = useMemo<Entitlements>(() => {
    if (!staff) return entitlements
    const has = staffAccess.split(',')
    return Object.fromEntries(MODULES.map((m) => [m.key, (entitlements?.[m.key] !== false) && (has.includes(m.key) || (m.key === 'research' && has.includes('inventory')) || (m.key === 'reports' && has.includes('marketing')))]))
  }, [staff, staffAccess, entitlements])
  const modules = modulesFor(user?.role, allowed, user?.accountRole)
  // A teammate who lands somewhere their login does not cover (the Finance overview at /,
  // an old link) is taken to the first page they can use, rather than a page of refusals.
  const moduleKeys = modules.map((m) => m.key).join(',')
  useEffect(() => {
    if (!staff || !user || !moduleKeys) return
    const here = moduleOf(pathname)
    if (here && moduleKeys.split(',').includes(here)) return
    const first = navFor(user.role, moduleKeys.split(',')[0] as ModuleKey, allowed, user.accountRole)[0]
    if (first && first.to !== pathname) navigate(first.to, { replace: true })
  }, [staff, user, moduleKeys, pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * What the automations have handed this person, on every page. The to-dos are meant
   * for people who spend their day in Appointments or Finance, not in Automations, so
   * the count has to follow them there. Checked each minute and whenever the inbox
   * page changes something.
   */
  // The inbox is the automations' output: hidden with the module (lib/nav.ts HIDDEN_MODULES).
  const hasInbox = (user?.role === 'admin' || user?.role === 'data_admin') && allowed?.automations !== false && !isHidden('automations') && !staff
  const hasChat = (user?.role === 'admin' || user?.role === 'data_admin') && allowed?.chat !== false
  const [chatCount, setChatCount] = useState({ waiting: 0, unread: 0 })
  useEffect(() => {
    if (!hasChat) return
    const check = () => api.get<{ waiting: number; unread: number }>('/chat/badge')
      .then((r) => setChatCount(r.data)).catch(() => { /* a badge is not worth an error */ })
    check()
    const t = setInterval(check, 20000)
    window.addEventListener('chat-changed', check)
    // Live: a new message anywhere updates the badge within a moment, not at the next poll.
    let soon: ReturnType<typeof setTimeout> | null = null
    const off = staffLive()?.on((e) => {
      if (e.type !== 'message' || soon) return
      soon = setTimeout(() => { soon = null; check() }, 400)
    })
    return () => { clearInterval(t); window.removeEventListener('chat-changed', check); off?.(); if (soon) clearTimeout(soon) }
  }, [hasChat])
  const [inboxCount, setInboxCount] = useState(0)
  useEffect(() => {
    if (!hasInbox) return
    const check = () => api.get<{ open: number }>('/automations/inbox/count')
      .then((r) => setInboxCount(r.data.open)).catch(() => { /* a badge is not worth an error */ })
    check()
    const t = setInterval(check, 60000)
    window.addEventListener('inbox-changed', check)
    return () => { clearInterval(t); window.removeEventListener('inbox-changed', check) }
  }, [hasInbox])

  // The page you are on decides the module — landing on /debtors from a link must
  // not leave the sidebar showing Payroll. Only when the path says nothing does the
  // remembered choice apply, and only then the first module.
  /**
   * ⚠️ MOST SPECIFIC WINS. NavLink's own `isActive` is a prefix match, and "/booking" is
   * a prefix of "/booking/openings", so opening Open space lit up Book a patient as
   * well. Turning `end` on everywhere is not the fix either: /booking/:practitionerId
   * SHOULD keep Book a patient lit.
   *
   * So: every item whose path the URL sits inside is a candidate, and the longest one
   * wins. "/" is exact-match only, or it would claim every page in the app.
   */
  const role = roleLabel(user)
  const shownName = displayName(user) || role || 'Signed in'

  const activeTo = useMemo(() => {
    const hits = navFor(user?.role, undefined, allowed, user?.accountRole).filter((i) =>
      i.to === '/' ? pathname === '/'
                   : pathname === i.to || pathname.startsWith(`${i.to}/`))
    return hits.sort((a, b) => b.to.length - a.to.length)[0]?.to
  }, [pathname, user?.role, entitlements])

  const fromPath = moduleOf(pathname)
  const [chosen, setChosen] = useState<ModuleKey | undefined>(() => {
    try {
      const saved = localStorage.getItem(REMEMBERED) as ModuleKey | null
      return saved && modules.some((m) => m.key === saved) ? saved : undefined
    } catch { return undefined }   // private windows throw on read
  })
  const active = fromPath ?? chosen ?? modules[0]?.key
  useEffect(() => {
    if (!fromPath) return
    setChosen(fromPath)
    try { localStorage.setItem(REMEMBERED, fromPath) } catch { /* not worth failing over */ }
  }, [fromPath])

  const items = navFor(user?.role, active, allowed, user?.accountRole)
  const sections = [...new Set(items.map((i) => i.section))]
  const current = navFor(user?.role, undefined, allowed, user?.accountRole).find((i) => i.to === pathname)
  /**
   * Pages one level below a menu entry — a patient's purchases, a clinician's booking
   * page — have no nav item of their own, so the header used to fall back to the app's
   * name and read "Inventory · Lynqpod". Name them here instead. Deliberately NOT a
   * longest-prefix match on the menu: that would title a patient's page "Overview".
   */
  const DEEP: Array<[RegExp, string]> = [
    [/^\/inventory\/patient\//, 'Patient purchases'],
    [/^\/team\/[a-f0-9]{24}$/, 'Clinician'],
    [/^\/team\/person\/nurse\/[a-f0-9]{24}$/, 'Nurse'],
    [/^\/team\/person\/teammate\/[a-f0-9]{24}$/, 'Teammate'],
    [/^\/automations\/new$/, 'New automation'],
    [/^\/automations\/[a-f0-9]{24}$/, 'Automation'],
    [/^\/chat\/[a-f0-9]{24}$/, 'Conversations'],
    [/^\/chat\/flows\/.+$/, 'Flow'],
    // Off the menu since the funnel moved onto the Ads report, but still reachable from there
    // and from bookmarks, so it still needs a name in the top bar.
    [/^\/marketing\/funnel$/, 'Booking funnel'],
  ]
  const deepLabel = DEEP.find(([rx]) => rx.test(pathname))?.[1]
  // Settings sits outside the modules, so its pages name themselves rather than borrowing
  // whichever module was open last.
  const inSettings = pathname === '/settings' || pathname.startsWith('/settings/')
  const canSettings = user?.role === 'data_admin' && ['practice_manager', 'ceo'].includes(user?.accountRole ?? '')
  /*
   * Settings holds the three things that belong to the person and the practice rather than
   * to a module. Change password is open to everyone signed in, on purpose: it used to live
   * inside Finance, where reception and clinicians cannot go, so they had to ask somebody
   * else to change their own password.
   */
  const SETTINGS_ITEMS = [
    { to: '/settings/password', label: 'Change password', show: !!user,
      icon: 'M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2Zm2-10V7a4 4 0 1 1 8 0v4' },
    /*
     * Somebody ELSE'S password, for an admin who has to let a colleague back in. Different
     * thing from Change password above, and it used to hide inside Finance.
     */
    { to: '/settings/reset-password', label: "Reset someone's password", show: user?.role === 'admin' || user?.role === 'data_admin',
      icon: 'M15 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm-4 7c-3.3 0-6 2-6 4.5V21h9M17 14v6m3-3h-6' },
    { to: '/settings/activity', label: 'Activity log', show: user?.role === 'admin' || user?.role === 'data_admin',
      icon: 'M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' },
    { to: '/settings/apps', label: 'Connected apps', show: canSettings,
      icon: 'M4 7h6v6H4V7Zm10 0h6v6h-6V7ZM4 17h6v4H4v-4Zm10 0h6v4h-6v-4Z' },
  ].filter((i) => i.show)
  const SETTINGS_PAGES: Record<string, string> = Object.fromEntries(SETTINGS_ITEMS.map((i) => [i.to, i.label]))
  const activeModule = modules.find((m) => m.key === active)

  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc) }
  }, [open])

  /** Switching goes to that module's first page, so the pane is never left empty. */
  function switchTo(key: ModuleKey) {
    setOpen(false)
    setChosen(key)
    try { localStorage.setItem(REMEMBERED, key) } catch { /* ignore */ }
    const first = navFor(user?.role, key, allowed, user?.accountRole)[0]
    if (first && first.to !== pathname) navigate(first.to)
  }

  return (
    /**
     * ⚠️ THE WINDOW DOES NOT SCROLL; THE CONTENT DOES.
     *
     * h-screen with overflow-hidden here, and the only scrollable region is <main>. The
     * sidebar and the top bar stay put, so the module you are in and the page you are on
     * are always on screen. The whole document used to scroll and both scrolled away,
     * which on a long table meant losing track of where you were.
     *
     * ⚠️ EVERY window.scrollTo IN THIS FILE HAD TO MOVE WITH IT. Once a container
     * scrolls, window.scrollY is permanently 0, so the reset and the BACK restore would
     * have gone on running and silently done nothing at all.
     */
    <div className="flex h-screen overflow-hidden">
      <ProgressBar />
      <style>{`
        @keyframes navPop { from { opacity: 0; transform: translateY(-4px) scale(.985) } to { opacity: 1; transform: none } }
        /* backwards, NOT both: a fill that keeps the last keyframe leaves an
           identity transform on this element forever, and a transformed element is
           the frame every position:fixed descendant is measured against. That is how
           a modal ended up centred halfway down a page instead of on the screen. */
        .page-in { animation: pageIn .22s cubic-bezier(.16,1,.3,1) backwards }
        @keyframes pageIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) { .page-in { animation: none } }
        @media (prefers-reduced-motion: reduce) { [style*="navPop"] { animation: none !important } }
      `}</style>
      {/*
        * The sidebar. Three things it has to do, in order: say which practice module you
        * are in, get you to a page, and say who you are signed in as.
        *
        * ⚠️ THE ACTIVE ITEM CARRIES A RAIL, not just a tint. A background wash alone is
        * easy to lose against a hover state, and hover and selected looking similar is
        * how people end up clicking to check where they are. The rail is a second,
        * unambiguous signal, and the icon takes the brand colour with it, so the state
        * is never carried by one thin cue.
        *
        * Section labels are quieter than they were: they are scaffolding, and at 11px
        * semibold slate-400 they were competing with the items underneath them.
        */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div ref={box} className="relative p-2.5">
          <button onClick={() => modules.length > 1 && setOpen((o) => !o)}
            disabled={modules.length < 2}
            className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
              modules.length > 1 ? 'hover:bg-slate-100' : 'cursor-default'}`}>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-600 text-xs font-bold text-white shadow-sm">
              {activeModule?.mark ?? 'L'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold leading-tight tracking-tight text-slate-900">
                {activeModule?.label ?? 'Lynqpod'}
              </span>
              <span className="block text-[0.68rem] leading-tight text-slate-400">
                {modules.length > 1 ? 'Switch module' : 'Lynqpod'}
              </span>
            </span>
            {modules.length > 1 && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
                <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>

          {open && (
            <div className="absolute left-2 right-2 top-[58px] z-20 origin-top rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
                 style={{ animation: 'navPop .16s cubic-bezier(.16,1,.3,1) both' }}>
              {modules.map((m) => (
                <button key={m.key} onClick={() => switchTo(m.key)}
                  className={`flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                    m.key === active ? 'bg-brand-50' : 'hover:bg-slate-50'}`}>
                  <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md text-[0.68rem] font-bold ${
                    m.key === active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {m.mark}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-sm ${m.key === active ? 'font-semibold text-brand-700' : 'font-medium text-slate-800'}`}>
                      {m.label}
                    </span>
                    <span className="block text-xs leading-snug text-slate-500">{m.blurb}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mx-2.5 border-t border-slate-100" />

        {/*
          * A plain Link, not NavLink. NavLink decides "active" by prefix and stamps
          * aria-current from its OWN answer, so /booking claimed /booking/openings, and
          * passing undefined only fell back to its default of "page". `activeTo` above
          * is the single source of truth for both the styling and the attribute.
          */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-3">
          {inSettings ? (
            <div>
              <p className="px-2.5 pb-1 text-[0.68rem] font-medium uppercase tracking-[0.12em] text-slate-400">Settings</p>
              {SETTINGS_ITEMS.map((i) => {
                const on = i.to === pathname
                return (
                  <Link key={i.to} to={i.to} aria-current={on ? 'page' : undefined}
                    className={`group relative flex items-center gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-sm transition-colors ${
                      on ? 'bg-brand-50 font-semibold text-brand-700'
                         : 'font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
                    <span aria-hidden="true"
                      className={`absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-600 transition-opacity ${
                        on ? 'opacity-100' : 'opacity-0'}`} />
                    <span className={on ? 'text-brand-600' : 'text-slate-400 transition-colors group-hover:text-slate-500'}>
                      <Icon d={i.icon} />
                    </span>
                    <span className="truncate">{i.label}</span>
                  </Link>
                )
              })}
            </div>
          ) : sections.map((s, si) => (
            <div key={s} className={si ? 'mt-5' : ''}>
              <p className="px-2.5 pb-1 text-[0.68rem] font-medium uppercase tracking-[0.12em] text-slate-400">{s}</p>
              {items.filter((i) => i.section === s).map((i) => {
                const on = i.to === activeTo
                return (
                  <Link key={i.to} to={i.to} aria-current={on ? 'page' : undefined}
                    className={`group relative flex items-center gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-sm transition-colors ${
                      on ? 'bg-brand-50 font-semibold text-brand-700'
                         : 'font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
                    {/* The rail. Absolute so it never shifts the label, which would make
                        the whole list twitch as you move between pages. */}
                    <span aria-hidden="true"
                      className={`absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-600 transition-opacity ${
                        on ? 'opacity-100' : 'opacity-0'}`} />
                    <span className={on ? 'text-brand-600' : 'text-slate-400 transition-colors group-hover:text-slate-500'}>
                      <Icon d={i.icon} />
                    </span>
                    <span className="truncate">{i.label}</span>
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-100 p-2.5">
          <div className="flex items-center gap-2.5 rounded-lg px-2.5 py-2">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
              {initials(shownName)}
            </span>
            {/* The role line is dropped when it repeats the name. An account literally
                called "Admin" whose role is "Admin" printed the word twice, which reads
                as a bug rather than as information. */}
            {/* ⚠️ COMPARE AGAINST WHAT IS RENDERED, not against displayName. When
                displayName is empty the name line falls back to the role, and comparing
                the role to the empty displayName still said "different", so it printed
                "Admin / Admin" anyway. That is the second time this bug shipped. */}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium leading-tight text-slate-900">{shownName}</span>
              {role && role.toLowerCase() !== shownName.toLowerCase() && (
                <span className="block truncate text-xs leading-tight text-slate-500">{role}</span>
              )}
            </span>
          </div>
          {/* Settings lives here, outside the modules: it is about the practice, not about
              any one module. Only Connected apps so far, so only practice managers and CEOs
              see it (lib/auth accountRole; the server checks too, routes/apps.routes.js). */}
          {SETTINGS_ITEMS.length > 0 && (
            <Link to={SETTINGS_ITEMS[0].to} aria-current={inSettings ? 'page' : undefined}
              className={`mt-0.5 flex w-full items-center gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-left text-sm font-medium transition-colors ${
                inSettings ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}>
              <span className={inSettings ? 'text-brand-600' : 'text-slate-400'}>
                <Icon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-3a8 8 0 0 1-.1 1.2l2 1.6-2 3.4-2.4-1a8 8 0 0 1-2 1.2l-.4 2.6h-4l-.4-2.6a8 8 0 0 1-2-1.2l-2.4 1-2-3.4 2-1.6A8 8 0 0 1 4 12c0-.4 0-.8.1-1.2l-2-1.6 2-3.4 2.4 1a8 8 0 0 1 2-1.2L8.9 3h4l.4 2.6a8 8 0 0 1 2 1.2l2.4-1 2 3.4-2 1.6c.1.4.1.8.1 1.2Z" />
              </span>
              Settings
            </Link>
          )}
          <button onClick={signOut}
            className="mt-0.5 flex w-full items-center gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-left text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
            <span className="text-slate-400">
              <Icon d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />
            </span>
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          * The product's identity lives here rather than in the sidebar. Stacked above
          * the module switcher it made two identity rows compete for the same corner;
          * here the sidebar is purely "where am I going" and the top bar is "what this
          * is, and where I am".
          */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="flex min-w-0 items-center gap-3.5">
            <Brand size={24} />
            <span className="h-5 w-px shrink-0 bg-slate-200" aria-hidden="true" />
          <h1 className="flex items-baseline gap-2 text-sm font-semibold text-slate-900">
            {inSettings ? <span className="text-xs font-normal text-slate-400">Settings</span>
              : activeModule && <span className="text-xs font-normal text-slate-400">{activeModule.label}</span>}
            {inSettings ? (SETTINGS_PAGES[pathname] ?? 'Settings') : (current?.label ?? deepLabel ?? 'Lynqpod')}
          </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Search across the app, Ctrl+S (components/SearchPalette.tsx); not for the platform console. */}
            {user && user.role !== 'platform_admin' && <SearchPalette role={user.role} accountRole={user.accountRole} entitlements={allowed} />}
            <p className="text-xs text-slate-500">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {hasChat && (
              <Link to="/chat" title={chatCount.waiting ? `${chatCount.waiting} waiting for a person` : 'Chat'}
                aria-label={`Chat, ${chatCount.waiting} waiting, ${chatCount.unread} unread`}
                className="relative rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
                </svg>
                {chatCount.waiting + chatCount.unread > 0 && (
                  <span className={`tnum absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[0.625rem] font-semibold text-white ${chatCount.waiting ? 'bg-amber-500' : 'bg-brand-600'}`}>
                    {chatCount.waiting + chatCount.unread}
                  </span>
                )}
              </Link>
            )}
            {hasInbox && (
              <Link to="/automations/inbox" aria-label={`Inbox, ${inboxCount} waiting`} title="Your inbox"
                className="relative rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 13h4l2 3h6l2-3h4M5.5 5h13l2.5 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6l2.5-8Z" />
                </svg>
                {inboxCount > 0 && (
                  <span className="tnum absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[0.625rem] font-semibold text-white">
                    {inboxCount > 99 ? '99+' : inboxCount}
                  </span>
                )}
              </Link>
            )}
          </div>
        </header>
        {/* Keyed on the path so the animation replays per page. A query change is not a
            new page, so filtering a table does not make it flash. */}
        <main ref={scroller} className="min-w-0 flex-1 overflow-y-auto p-6">
          {/* The setting-up counter used to sit here on every page. It counted things the
              practice had not done, above whatever they had actually come to do, and could
              only be hidden one visit at a time. The checklist itself is still at
              /getting-started for anyone who wants it. */}
          <div key={pathname} className="page-in"><FindOnPage />
            <Outlet /></div>
        </main>
      </div>
    </div>
  )
}
